import { useState, useCallback, useEffect } from 'react';
import { CssVarsProvider } from '@mui/joy/styles';
import CssBaseline from '@mui/joy/CssBaseline';
import Box from '@mui/joy/Box';
import { useLocation } from 'react-router-dom';

import Sidebar from './components/Nav/Sidebar';
import MainAppPanel from './components/MainAppPanel';

import customTheme from './lib/theme';
import secretPurpleTheme from './lib/secretPurpleTheme';
import { deriveApiBaseUrl } from './lib/api-client/urls';

import './styles.css';

import AnnouncementBanner from './components/Shared/AnnouncementBanner';
import InsecurePasswordBanner from './components/Shared/InsecurePasswordBanner';
import StartupWizard from './components/Shared/StartupWizard';
import VersionUpdateBanner from './components/Shared/VersionUpdateBanner';
import { NotificationProvider } from './components/Shared/NotificationSystem';
import { ExperimentInfoProvider } from './lib/ExperimentInfoContext';
import * as chatAPI from './lib/transformerlab-api-sdk';
import { AuthProvider, useAuth } from './lib/authContext';
import LoginPage from './components/Login/LoginPage';
import { AnalyticsProvider } from './components/Shared/analytics/AnalyticsContext';
import FullPageLoader from './components/Shared/FullPageLoader';
import ConnectionLostModal from './components/Shared/ConnectionLostModal';
import InvitationLanding from './components/Team/InvitationLanding';
import PublicShareViewer from './components/PublicShare/PublicShareViewer';

type AppContentProps = {
  connection: string;
  setConnection: (conn: string) => void;
  setLogsDrawerOpen: (open: boolean) => void;
  themeSetter: (name: string) => void;
};

function AppContent({
  connection,
  setConnection,
  setLogsDrawerOpen,
  themeSetter,
}: AppContentProps) {
  const location = useLocation();
  const authContext = useAuth();
  const { isError: connectionHealthError, isLoading: connectionHealthLoading } =
    chatAPI.useConnectionHealth(connection);
  const isInvitePage = location.pathname === '/invite';
  const isPublicSharePage = location.pathname.startsWith('/public/share/');

  const showConnectionLostModal =
    connection !== '' && !connectionHealthLoading && !!connectionHealthError;

  // While auth is initializing or the initial user info is loading, show a full-page loader.
  // We only block on the *first* user load (no user/error yet) so that revalidation
  // or transient refetches don't cause the login page to briefly disappear.
  const isInitialUserLoad =
    authContext.userIsLoading && !authContext.user && !authContext.userError;

  useEffect(() => {
    if (authContext.isAuthenticated) {
      const redirect = localStorage.getItem('redirectAfterLogin');
      if (redirect) {
        localStorage.removeItem('redirectAfterLogin');
        window.location.hash = redirect;
      }
    }
  }, [authContext.isAuthenticated]);

  useEffect(() => {
    if (isInvitePage && !authContext.isAuthenticated) {
      const currentHash = window.location.hash;
      if (currentHash) {
        localStorage.setItem('redirectAfterLogin', currentHash);
      }
    }
  }, [isInvitePage, authContext.isAuthenticated]);

  if (isPublicSharePage) {
    return <PublicShareViewer />;
  }

  if (isInvitePage) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          width: '100vw',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
          backgroundColor: 'background.body',
        }}
      >
        <InvitationLanding />
      </Box>
    );
  }

  if (authContext.initializing || isInitialUserLoad) {
    return <FullPageLoader />;
  }

  if (!authContext?.isAuthenticated) {
    const currentHash = window.location.hash;
    if (currentHash && currentHash !== '#/' && currentHash !== '#') {
      localStorage.setItem('redirectAfterLogin', currentHash);
    }
    return <LoginPage />;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100dvh',
        width: '100dvw',
        overflow: 'hidden',
      }}
    >
      <InsecurePasswordBanner />
      <StartupWizard />
      <Box
        component="main"
        className="MainContent"
        sx={() => ({
          display: 'grid',
          flex: 1,
          width: '100%',
          overflow: 'hidden',
          gridTemplateColumns: '180px 1fr',
          gridTemplateRows: '1fr',
          gridTemplateAreas: `
          "sidebar main"
        `,
        })}
      >
        <Sidebar
          setLogsDrawerOpen={setLogsDrawerOpen as any}
          themeSetter={themeSetter}
        />
        <Box
          sx={{
            px: {
              sm: 1,
              md: 1,
              lg: 1,
            },
            pt: 1,
            pb: 0,
            height: '100%',
            gridArea: 'main',
            overflow: 'hidden',
            backgroundColor: 'var(--joy-palette-background-surface)',
            display: 'flex',
            flexDirection: 'column',
          }}
          id="main-app-panel"
        >
          <AnnouncementBanner />
          <VersionUpdateBanner />
          <MainAppPanel setLogsDrawerOpen={setLogsDrawerOpen as any} />
        </Box>
        {showConnectionLostModal && (
          <ConnectionLostModal
            connection={connection}
            setConnection={setConnection}
          />
        )}
      </Box>
    </Box>
  );
}

export default function App() {
  const initialApiUrl = deriveApiBaseUrl();

  const [connection, setConnection] = useState(initialApiUrl);
  const [, setLogsDrawerOpen] = useState(false);
  const [theme, setTheme] = useState(customTheme);

  useEffect(() => {
    window.TransformerLab = {};
    window.TransformerLab.API_URL = initialApiUrl;
  }, [initialApiUrl]);

  const themeSetter = useCallback((name: string) => {
    if (name === 'purple') {
      setTheme(secretPurpleTheme);
    } else {
      setTheme(customTheme);
    }
  }, []);

  return (
    <NotificationProvider>
      <CssVarsProvider disableTransitionOnChange theme={theme}>
        <CssBaseline />
        <AuthProvider connection={connection}>
          <AnalyticsProvider>
            <ExperimentInfoProvider connection={connection}>
              <AppContent
                connection={connection}
                setConnection={setConnection}
                setLogsDrawerOpen={setLogsDrawerOpen}
                themeSetter={themeSetter}
              />
            </ExperimentInfoProvider>
          </AnalyticsProvider>
        </AuthProvider>
      </CssVarsProvider>
    </NotificationProvider>
  );
}
